import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import {
  UsersAuthzUserPermissionDto,
  useUsersAuthzAllowPermissionToUserManagedPermissions,
  useUsersAuthzDenyPermissionToUserManagedPermissions,
  useUsersAuthzResetUserPermissionManagedPermissions,
} from '@mi-front/api-v2';
import { Box, Card, CardContent, Checkbox, FormControlLabel } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { PermissionItemComponentProps } from './ActionPermissionsView';
import { useLocalizedText } from './GroupPermissionItem';

interface Props extends PermissionItemComponentProps<UsersAuthzUserPermissionDto> {
  readonly userId: string;
  readonly groupId: string | null;
}

/**
 * Renders allow/deny/reset controls for a single user managed permission.
 */
export function UserPermissionItem(p: Props) {
  const { $t } = useIntl();
  const { userId, groupId, permissionState: initialUserPermission, permissionDefinition } = p;

  const [userPermission, setUserPermission] = useState(initialUserPermission);

  const mutationParams = {
    userId,
    permission: userPermission.permission,
  };

  const allowMutation = useUsersAuthzAllowPermissionToUserManagedPermissions({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data);
      },
    },
  });

  const denyMutation = useUsersAuthzDenyPermissionToUserManagedPermissions({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data);
      },
    },
  });

  const resetMutation = useUsersAuthzResetUserPermissionManagedPermissions({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data);
      },
    },
  });

  const isUpdating = allowMutation.isPending || denyMutation.isPending || resetMutation.isPending;

  const canInheritFromGroup = !!groupId;
  const inheritsFromGroup = !!userPermission.groupId;

  const onInheritFromGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inherit = event.target.checked;

    if (inherit) {
      resetMutation.mutate({ userId, permission: userPermission.permission });
    } else if (userPermission.allowed) {
      denyMutation.mutate(mutationParams);
    } else {
      allowMutation.mutate(mutationParams);
    }
  };

  const disablePermissionToggle = isUpdating || inheritsFromGroup;

  return (
    <Card>
      <CardContent>
        <Box sx={{ fontWeight: '500' }}>{useLocalizedText(permissionDefinition.description)}</Box>

        {canInheritFromGroup && (
          <Box display="flex" alignItems="center">
            <FormControlLabel
              sx={{ marginBottom: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={inheritsFromGroup}
                  onChange={onInheritFromGroupChange}
                  disabled={isUpdating}
                  sx={{ paddingRight: 0.5 }}
                />
              }
              label={
                <span style={{ fontSize: '14px' }}>
                  {$t({
                    id: 'userPermission.inheritsFromGroup',
                    defaultMessage: 'Hereda de grupo',
                  })}
                </span>
              }
            />
            {inheritsFromGroup && (
              <StatusSwitch
                checked={userPermission.allowed}
                disabled
                sx={{ marginBottom: 0 }}
                onChange={() => {
                  // noop
                }}
                size="small"
              />
            )}
          </Box>
        )}

        {!inheritsFromGroup && (
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <StatusSwitch
              checked={userPermission.allowed}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  allowMutation.mutate(mutationParams);
                } else {
                  denyMutation.mutate(mutationParams);
                }
              }}
              disabled={disablePermissionToggle}
              sx={{ marginBottom: 0 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
